import React, { Component, ErrorInfo, ReactNode } from 'react';

import {
  recordRenderError,
  summarizeError,
  RenderErrorRecord,
} from './renderErrorLog';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Static fallback node.
   *
   * THUB-290: prefer `renderFallback`, which RECEIVES the captured error. A static node cannot
   * say anything about what failed, which is exactly how a bare "Content Error" became
   * unactionable — an operator could report only the words on screen.
   */
  fallback?: ReactNode;
  /** Error-aware fallback. Takes precedence over `fallback`. */
  renderFallback?: (record: RenderErrorRecord, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  /** THUB-290: identity an operator can quote and we can query later. */
  record: RenderErrorRecord | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      record: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // THUB-290: build the record here rather than in getDerivedStateFromError, because the
    // component stack (the single most useful fact — WHICH component threw) only arrives with
    // errorInfo. Capture the path at throw time: the boundary resets on a pathname change, so
    // by the time anyone reads the record the user has usually navigated away.
    const record = summarizeError(
      error,
      errorInfo?.componentStack,
      typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    );

    // Persist BEFORE setState. If a later render throws again, the evidence is already durable.
    recordRenderError(record);

    this.setState({ error, errorInfo, record });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      // Reset error state if resetKeys changed
      if (resetKeys && prevProps.resetKeys) {
        const hasResetKeyChanged = resetKeys.some(
          (key, index) => key !== prevProps.resetKeys![index]
        );
        if (hasResetKeyChanged) {
          this.resetErrorBoundary();
        }
      }

      // Reset error state if any props changed
      if (resetOnPropsChange && prevProps !== this.props) {
        this.resetErrorBoundary();
      }
    }
  }

  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      record: null
    });
  };

  render() {
    const { hasError, error, errorInfo, record } = this.state;
    const { children, fallback, renderFallback } = this.props;

    if (hasError) {
      // THUB-290: an error-aware fallback wins, so the caller can show the identity instead of
      // a fixed sentence. `record` is set in componentDidCatch, one tick after
      // getDerivedStateFromError flips hasError — hence the guard.
      if (renderFallback && record) {
        return renderFallback(record, this.resetErrorBoundary);
      }
      if (fallback) {
        return fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-[300px] p-8 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg my-4">
          <div className="text-center max-w-[500px]">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-danger-500 text-2xl font-semibold mb-4">Something went wrong</h2>
            <p className="text-neutral-500 dark:text-neutral-400 text-base mb-6 leading-relaxed">
              {error?.message || 'An unexpected error occurred'}
            </p>

            {/* THUB-290: identity an operator can quote without opening devtools. */}
            {record && (
              <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-4 font-mono">
                {record.name} in <strong>{record.component}</strong>
                {' · '}ref <strong>{record.correlationId}</strong>
              </p>
            )}

            {/* THUB-290: no longer gated on NODE_ENV. Gating the details on a development
                build meant DEV/TST/NP/PRD all discarded the only evidence of the failure,
                which is how a Content Error became a log-diving exercise. Collapsed by
                default, so it informs without shouting stack traces at a user. */}
            {errorInfo && (
              <details className="text-left my-6 p-4 bg-neutral-100 dark:bg-neutral-700 rounded border border-neutral-200 dark:border-neutral-600">
                <summary className="cursor-pointer font-semibold text-neutral-600 dark:text-neutral-300 mb-2 hover:text-primary-600 dark:hover:text-primary-400">
                  Technical details (for support)
                </summary>
                <pre className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-600 rounded p-3 my-2 font-mono text-sm leading-snug text-neutral-600 dark:text-neutral-300 overflow-x-auto whitespace-pre-wrap break-words">
                  {error?.stack}
                </pre>
                <pre className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-600 rounded p-3 my-2 font-mono text-sm leading-snug text-neutral-600 dark:text-neutral-300 overflow-x-auto whitespace-pre-wrap break-words">
                  {errorInfo.componentStack}
                </pre>
              </details>
            )}

            <button
              onClick={this.resetErrorBoundary}
              className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white border-none py-3 px-6 rounded text-base font-medium cursor-pointer transition-colors duration-200 focus:outline-2 focus:outline-primary-500 focus:outline-offset-2"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}

// Functional wrapper for easier use with hooks
interface ErrorBoundaryWrapperProps extends Omit<ErrorBoundaryProps, 'resetKeys'> {
  resetKeys?: Array<string | number>;
}

export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: ErrorBoundaryWrapperProps
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}